import { Children, isValidElement, useMemo, useState } from 'react';

function collectText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(collectText).join('');
  if (isValidElement(node)) return collectText(node.props.children);
  return '';
}

export default function DocsPromptBlock({ children, surfaceOnly = null, forcePrompt = false }) {
  const [surface, setSurface] = useState('client');
  const [copied, setCopied] = useState(false);
  const prompt = useMemo(() => collectText(Children.toArray(children)).trim(), [children]);
  const firstChild = Children.toArray(children)[0];
  const language = isValidElement(firstChild)
    ? firstChild.props.className?.match(/language-([^\s]+)/)?.[1]
    : null;
  const isPrompt = forcePrompt || language === 'prompt' || language === 'text';

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(prompt);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-gray-100 dark:border-white dark:bg-white dark:text-gray-900">
      <div className="flex min-h-[44px] items-center justify-between border-b border-white/10 px-3 dark:border-gray-200">
        {!isPrompt ? (
          <span className="px-2 font-mono text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {language || 'Example'}
          </span>
        ) : surfaceOnly ? (
          <span className="px-2 text-[12px] font-medium text-white dark:text-gray-950">AI client</span>
        ) : <div className="flex self-stretch" aria-label="Prompt surface">
          {[
            ['client', 'AI client'],
            ['pabs', 'Pabs Chat'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSurface(value)}
              className={`relative px-3 text-[12px] font-medium transition-colors ${
                surface === value
                  ? 'text-white dark:text-gray-950'
                  : 'text-gray-500 hover:text-gray-200 dark:text-gray-500 dark:hover:text-gray-800'
              }`}
            >
              {label}
              {surface === value && (
                <span className="absolute inset-x-3 bottom-0 h-0.5 bg-emerald-400 dark:bg-emerald-600" />
              )}
            </button>
          ))}
        </div>}
        <button
          type="button"
          onClick={copyPrompt}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-gray-400 transition-colors hover:bg-white/10 hover:text-white dark:text-gray-500 dark:hover:bg-gray-100 dark:hover:text-gray-900"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="px-5 py-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {isPrompt
            ? ((surfaceOnly || surface) === 'client' ? 'Paste into Claude, Codex, Cursor, or another connected client' : 'Paste into the plan chat')
            : 'Copy this value'}
        </p>
        <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[13px] leading-relaxed text-gray-100 dark:text-gray-900">
          {prompt}
        </pre>
      </div>
    </div>
  );
}
